import { useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  INotificationBaseMessage,
  NotificationType,
} from "@evennt-tech/api-v2-core";
import uniqid from "uniqid";
import { RequestKey } from "api/request-key";
import { apiV2 } from "api/firebase";

/**
 * The list changes rarely, yet the consumers mount often:
 * useIframeStatusOverlay runs inside every RichTextEditor, so without a
 * staleTime each task-drawer open refired the request. The long staleTime
 * is safe because every way the list can change invalidates the cache:
 * own mutations do it directly (useVerifiedDomainMutations), and writes
 * from other sessions (e.g. an admin approving a domain) arrive through
 * the notification-service events below.
 */
const STALE_TIME = 5 * 60_000;
const CACHE_TIME = 10 * 60_000;

/**
 * Published by the API on every verified-domain write — create, admin
 * approve/reject (update), delete — globally, WITHOUT a showId (see
 * apps/api/src/core/verified-domain/services/verified-domain.service.ts),
 * so the subscription below uses the gateway's common (show-less) channel.
 */
const VERIFIED_DOMAIN_EVENTS = [
  NotificationType.CREATED_VERIFIED_DOMAIN,
  NotificationType.UPDATED_VERIFIED_DOMAIN,
  NotificationType.DELETED_VERIFIED_DOMAIN,
];

export const useMyVerifiedDomains = (enabled = true) => {
  const queryClient = useQueryClient();
  // Per-instance listener id: several consumers can be mounted at once
  // (drawer editor + subtask-modal editor + iframe modal). The gateway
  // keys callbacks by `key + listenerId`, so with a SHARED id the first
  // unmount would tear the subscription down for the survivors.
  const listenerIdRef = useRef(`USE_MY_VERIFIED_DOMAINS:${uniqid()}`);

  useEffect(() => {
    const listenerId = listenerIdRef.current;
    const invalidate = async () => {
      await queryClient.invalidateQueries({
        queryKey: [RequestKey.GetMyVerifiedDomains],
      });
    };
    for (const key of VERIFIED_DOMAIN_EVENTS) {
      apiV2.notification.subscribe<INotificationBaseMessage>({
        key,
        listenerId,
        callback: invalidate,
      });
    }
    return () => {
      for (const key of VERIFIED_DOMAIN_EVENTS) {
        apiV2.notification.unsubscribe({ key, listenerId });
      }
    };
  }, [queryClient]);

  const { data, isFetching, isLoading } = useQuery({
    queryKey: [RequestKey.GetMyVerifiedDomains],
    refetchOnWindowFocus: false,
    enabled,
    queryFn: () => apiV2.verifiedDomain.getMy(),
    // NOT initialData: that would seed the CACHE as fresh and the first
    // mount would skip the fetch for a whole staleTime window.
    placeholderData: [],
    staleTime: STALE_TIME,
    cacheTime: CACHE_TIME,
  });

  return [data ?? [], isLoading || isFetching] as const;
};
